// Views and marks, as far as the rules go. A View is any picture of the job; a shop drawing is
// the kind Spencer verifies the items against. The job's items are the record: a drawing is a
// picture of them, never the other way round.

export interface ViewKind {
  key: string;
  label: string;
  /** True for the kind the two review passes verify against (the shop drawing). */
  verifies?: boolean;
  /** True for the kind that is a top-down picture of the job (2026-10-24: Overview is a kind, no longer a switch); its spots jump to the view of each wall. */
  overview?: boolean;
}

export interface ViewLike {
  id: string;
  kind?: string | null;
  supersedes_view_id?: string | null;
  /** Removed from the job (2026-10-24): out of every list, its marks with it, back with Restore. */
  retired_at?: string | null;
  is_overview?: boolean | null;
}

export interface MarkLike {
  view_id: string;
  job_item_id: string;
}

const same = (a: unknown, b: unknown) => String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

/** The kind a view carries, matched on the kind's key or its label. */
export function kindOf(view: ViewLike, kinds: ViewKind[]): ViewKind | undefined {
  return kinds.find((k) => same(k.key, view.kind) || same(k.label, view.kind));
}

/** The sheets in force: every view not removed that no later (live) sheet has replaced. A replacement removed hands the sheet back to the one it replaced. */
export function currentViews<T extends ViewLike>(views: T[]): T[] {
  const live = views.filter((v) => !v.retired_at);
  const replaced = new Set(live.map((v) => v.supersedes_view_id).filter(Boolean) as string[]);
  return live.filter((v) => !replaced.has(v.id));
}

/** A top-down picture: its kind is the one the view_kinds setting marks overview, or the older is_overview flag still set on it. */
export function isOverview(view: ViewLike, kinds: ViewKind[]): boolean {
  return kindOf(view, kinds)?.overview === true || view.is_overview === true;
}

/** The current views of the kind the passes verify against. */
export function verifyingViews<T extends ViewLike>(views: T[], kinds: ViewKind[]): T[] {
  return currentViews(views).filter((v) => kindOf(v, kinds)?.verifies === true);
}

/**
 * Pass 1's shop-drawing flag. Once a job has at least one shop-drawing view, every item that
 * needs a mark must have one on a shop-drawing view. A mark on a mockup or a photo does not count.
 */
export function unmarkedOnShopDrawings<I extends { id: string }>(
  items: I[],
  views: ViewLike[],
  marks: MarkLike[],
  kinds: ViewKind[],
): { applies: boolean; unmarked: I[] } {
  const sheets = new Set(verifyingViews(views, kinds).map((v) => v.id));
  if (sheets.size === 0) return { applies: false, unmarked: [] };
  const marked = new Set(marks.filter((m) => sheets.has(m.view_id)).map((m) => m.job_item_id));
  return { applies: true, unmarked: items.filter((i) => !marked.has(i.id)) };
}

export interface OldMark {
  /** The number the marked item carried on the old sheet. */
  number: string;
  x_pct: number;
  y_pct: number;
  /** A region mark's extent (2026-10-24), carried with it. */
  w_pct?: number | null;
  h_pct?: number | null;
}

/**
 * When a sheet is replaced for a new revision its marks carry over by item number. A mark
 * whose number is on no current item cannot carry and is reported, so someone places it again
 * or drops it. Two old marks for one number carry once.
 */
export function carryMarks<I extends { id: string; number: string }>(
  old: OldMark[],
  items: I[],
): { carried: { job_item_id: string; x_pct: number; y_pct: number; w_pct?: number; h_pct?: number }[]; lost: string[] } {
  const byNumber = new Map(items.map((i) => [i.number.trim().toLowerCase(), i]));
  const carried: { job_item_id: string; x_pct: number; y_pct: number; w_pct?: number; h_pct?: number }[] = [];
  const lost: string[] = [];
  const done = new Set<string>();
  for (const m of old) {
    const item = byNumber.get(m.number.trim().toLowerCase());
    if (!item) {
      if (!lost.includes(m.number)) lost.push(m.number);
      continue;
    }
    if (done.has(item.id)) continue;
    done.add(item.id);
    // a region carries its extent; a point carries none
    carried.push({ job_item_id: item.id, x_pct: m.x_pct, y_pct: m.y_pct, ...(m.w_pct != null && m.h_pct != null ? { w_pct: Number(m.w_pct), h_pct: Number(m.h_pct) } : {}) });
  }
  return { carried, lost };
}
